import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconEdit, IconPlus } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import type { ClientProfilePatch, ManagerClientDetail } from '@expeditor/shared';
import { managerClientsApi } from '../../shared/api/manager-clients';
import { EdoSubscriberField } from '../../shared/ui/EdoSubscriberField';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';
import { orderStatusColor, orderStatusLabel } from '../../shared/ui/order-labels';
import { useAuthStore } from '../../shared/auth/auth.store';
import { ResponsibleManagerCard } from './ResponsibleManagerCard';
import { ClientAccessCard } from './ClientAccessCard';
import { ClientIdentificationDetails } from './ClientIdentificationDetails';
import { AgreementsCard } from './AgreementsCard';

const TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Физическое лицо',
  ENTREPRENEUR: 'Индивидуальный предприниматель',
  LEGAL_RU: 'Юридическое лицо РФ',
  LEGAL_FOREIGN: 'Иностранная организация',
};

/**
 * Реквизитный адрес клиента — тот, что попадает в стороны документов
 * (см. clientToParty на бэке). Пустая строка = такого адреса нет, в документах
 * используется фактический.
 */
function clientRequisiteAddress(c: ManagerClientDetail | null): string {
  if (!c) return '';
  const d = (c.details ?? {}) as Record<string, unknown>;
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string).trim() : '');
  switch (c.type) {
    case 'LEGAL_RU':
      return s('legalAddress');
    case 'ENTREPRENEUR':
      return s('registrationAddress');
    case 'LEGAL_FOREIGN':
      return s('address');
    default:
      return '';
  }
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [c, setC] = useState<ManagerClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canActForClients = useAuthStore((s) => s.user?.canActForClients ?? false);

  // Правка данных клиента (общие поля).
  const [editOpen, setEditOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editEdo, setEditEdo] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Разбивка адреса (АдрРФ) для ЭТрН — по тому адресу, который идёт в документы:
  // реквизитному (юр./регистрации), а если его нет — фактическому.
  const requisiteAddress = clientRequisiteAddress(c);
  const { parts: addrParts, setParts: setAddrParts, save: saveAddrParts } = useAddressBreakdown(
    requisiteAddress || c?.actualAddress,
  );

  const reload = () => {
    if (!id) return;
    void managerClientsApi
      .getOne(id)
      .then(setC)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    if (!c) return;
    setEditDisplayName(c.displayName);
    setEditPhone(c.phone ?? '');
    setEditEmail(c.email ?? '');
    setEditAddress(c.actualAddress ?? '');
    setEditEdo(c.edoSubscriberId ?? '');
    setEditError(null);
    setEditOpen(true);
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setEditBusy(true);
    setEditError(null);
    const patch: ClientProfilePatch = {
      displayName: editDisplayName,
      phone: editPhone || undefined,
      email: editEmail || undefined,
      actualAddress: editAddress || undefined,
      edoSubscriberId: editEdo.trim(),
    };
    try {
      await managerClientsApi.updateProfile(id, patch);
      // Разбивка хранится отдельно, по тексту адреса.
      await saveAddrParts(requisiteAddress || editAddress).catch(() => undefined);
      setEditOpen(false);
      reload();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setEditBusy(false);
    }
  };

  if (error) return <Text c="red">{error}</Text>;
  if (!c) return <Text c="dimmed">Загрузка…</Text>;

  return (
    <Stack>
      <Anchor component={Link} to="/manager/clients" size="sm">
        ← К списку клиентов
      </Anchor>

      <Group justify="space-between">
        <Group>
          <Title order={2}>{c.displayName}</Title>
          <Code>{c.serviceId}</Code>
          {c.deregisteredAt ? (
            <Badge color="gray">регистрация прекращена</Badge>
          ) : c.isActive ? (
            <Badge color="green">активен</Badge>
          ) : (
            <Badge color="red">неактивен</Badge>
          )}
        </Group>
        {canActForClients && !c.deregisteredAt && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconEdit size={16} />}
              onClick={openEdit}
            >
              Редактировать данные
            </Button>
            <Button
              component={Link}
              to={`/manager/clients/${c.id}/orders/new`}
              leftSection={<IconPlus size={16} />}
            >
              Создать поручение
            </Button>
          </Group>
        )}
      </Group>

      <ResponsibleManagerCard client={c} onChange={setC} />

      <ClientAccessCard clientId={c.id} />

      <AgreementsCard clientId={c.id} />

      <SimpleGrid cols={{ base: 1, md: 3 }}>
        <Card withBorder>
          <Text size="xs" c="dimmed">
            Тип
          </Text>
          <Text fw={500}>{TYPE_LABEL[c.type] ?? c.type}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" c="dimmed">
            Поручений всего
          </Text>
          <Text fz="xl" fw={700}>
            {c.ordersTotal}
          </Text>
        </Card>
        <Card withBorder>
          <Text size="xs" c="dimmed">
            Зарегистрирован
          </Text>
          <Text fw={500}>{new Date(c.createdAt).toLocaleString('ru-RU')}</Text>
          {c.deregisteredAt && (
            <Text size="xs" c="dimmed" mt={4}>
              прекращена: {new Date(c.deregisteredAt).toLocaleString('ru-RU')}
            </Text>
          )}
        </Card>
      </SimpleGrid>

      <Card withBorder>
        <Title order={4} mb="sm">
          Идентификация
        </Title>
        <ClientIdentificationDetails client={c} />
      </Card>

      <Card withBorder>
        <Title order={4} mb="sm">
          Последние поручения ({c.recentOrders.length})
        </Title>
        {c.recentOrders.length === 0 ? (
          <Text c="dimmed" size="sm">
            Поручений нет.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Номер</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th>Груз</Table.Th>
                <Table.Th>Вес, кг</Table.Th>
                <Table.Th>Создано</Table.Th>
                <Table.Th>Подано</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {c.recentOrders.map((o) => (
                <Table.Tr key={o.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/manager/orders/${o.id}`}>
                      {o.number}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={orderStatusColor(o.status)}>{orderStatusLabel(o.status)}</Badge>
                  </Table.Td>
                  <Table.Td>{o.cargoName}</Table.Td>
                  <Table.Td>{o.cargoWeightKg}</Table.Td>
                  <Table.Td>{new Date(o.createdAt).toLocaleDateString('ru-RU')}</Table.Td>
                  <Table.Td>
                    {o.submittedAt ? new Date(o.submittedAt).toLocaleDateString('ru-RU') : '—'}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card withBorder>
        <Title order={4} mb="sm">
          Договоры ({c.contracts.length})
        </Title>
        {c.contracts.length === 0 ? (
          <Text c="dimmed" size="sm">
            Договоров нет.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Договор</Table.Th>
                <Table.Th>Поручение</Table.Th>
                <Table.Th>Подписан</Table.Th>
                <Table.Th>Перевозка</Table.Th>
                <Table.Th>Завершён</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {c.contracts.map((k) => (
                <Table.Tr key={k.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/manager/contracts/${k.id}`}>
                      {k.number}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Code>{k.orderNumber}</Code>
                  </Table.Td>
                  <Table.Td>{new Date(k.signedAt).toLocaleDateString('ru-RU')}</Table.Td>
                  <Table.Td>
                    {k.shipmentStatus ? <Badge>{k.shipmentStatus}</Badge> : '—'}
                  </Table.Td>
                  <Table.Td>
                    {k.endedAt ? (
                      new Date(k.endedAt).toLocaleDateString('ru-RU')
                    ) : (
                      <Badge color="green">действует</Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card withBorder>
        <Title order={4} mb="sm">
          Платежи (сводка)
        </Title>
        {c.paymentsSummary.length === 0 ? (
          <Text c="dimmed" size="sm">
            Платежей нет.
          </Text>
        ) : (
          <Group>
            {c.paymentsSummary.map((s) => (
              <Card key={s.currency} withBorder padding="xs">
                <Text size="xs" c="dimmed">
                  {s.currency} · {s.paymentsCount} операций
                </Text>
                <Text fz="lg" fw={700}>
                  {s.totalAmount.toLocaleString('ru-RU')} {s.currency}
                </Text>
              </Card>
            ))}
          </Group>
        )}
      </Card>

      <Modal
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title="Редактирование данных клиента"
        size="md"
      >
        <form onSubmit={submitEdit}>
          <Stack>
            <TextInput
              label="Наименование / ФИО"
              required
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.currentTarget.value)}
            />
            <TextInput
              label="Телефон"
              value={editPhone}
              onChange={(e) => setEditPhone(e.currentTarget.value)}
            />
            <TextInput
              label="Email"
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Фактический адрес"
              value={editAddress}
              onChange={(e) => setEditAddress(e.currentTarget.value)}
            />
            <AddressBreakdownFields
              value={addrParts}
              onChange={setAddrParts}
              hint={
                requisiteAddress
                  ? `Нужна для ЭТрН — адрес уходит в XML структурированно (АдрРФ). Разбивка для адреса: ${requisiteAddress}`
                  : 'Нужна для ЭТрН — адрес уходит в XML структурированно (АдрРФ). Разбивка для фактического адреса.'
              }
            />
            <EdoSubscriberField
              value={editEdo}
              onChange={setEditEdo}
              inn={c.primaryId}
              description="Код абонента клиента в ЭДО.МИГ24. «Найти» ищет по ИНН клиента."
            />
            <Text size="xs" c="dimmed">
              Реквизиты (ИНН/КПП/ОГРН и т.п.) меняются отдельно — обратитесь к
              администратору. Изменения фиксируются в журнале.
            </Text>
            {editError && <Alert color="red">{editError}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={editBusy}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
